// Site form modal component
// Form for creating or editing sites
// CHANGE: Added ReactDOM.createPortal to render modal at document root level
// CHANGE: Standardized modal dimensions and added max-height with scrollable content
// CHANGE: Added click-outside-to-close and Escape key handlers
// CHANGE: Added ARIA attributes for accessibility
// CHANGE: Added dirty state detection and unsaved changes warning
// UPDATED: Migrated to centralized toast system for error messages
// UPDATED: Standardized button loading text to "En cours..."
// FIXED: Removed undefined error variable reference
// CHANGE: Refactored to use reusable Modal component instead of inline modal

import { useState, useEffect } from 'react';
import type { Site } from '../../lib/supabase';
import { ConfirmationDialog } from '../ui/ConfirmationDialog';
import { useToast } from '../../hooks/useToast';
import { Modal } from '../ui/Modal';

interface SiteFormModalProps {
  site?: Site;
  onClose: () => void;
  onSubmit: (name: string) => Promise<void>;
}

export function SiteFormModal({ site, onClose, onSubmit }: SiteFormModalProps) {
  const [name, setName] = useState(site?.name || '');
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
  const { showToast } = useToast();

  const isEditing = !!site;
  const initialName = site?.name || '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      showToast('Le nom du site est requis', 'error');
      return;
    }

    // For editing, show confirmation dialog
    if (isEditing) {
      setShowConfirmation(true);
      return;
    }

    // For creating, proceed directly
    await performSubmit();
  };

  const performSubmit = async () => {
    setLoading(true);
    try {
      await onSubmit(name.trim());
    } catch (err) {
      showToast('Une erreur est survenue', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setShowConfirmation(false);
    await performSubmit();
  };

  const hasUnsavedChanges = (): boolean => {
    return name.trim() !== initialName.trim();
  };

  const handleCloseAttempt = () => {
    if (hasUnsavedChanges()) {
      setShowUnsavedChangesDialog(true);
    } else {
      onClose();
    }
  };

  // Handle Escape key for nested dialogs
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) {
        if (showConfirmation) {
          setShowConfirmation(false);
        } else if (showUnsavedChangesDialog) {
          setShowUnsavedChangesDialog(false);
        } else {
          handleCloseAttempt();
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [loading, showConfirmation, showUnsavedChangesDialog, name]);

  return (
    <>
      <Modal
        isOpen={true}
        onClose={onClose}
        title={isEditing ? 'Renommer le site' : 'Créer un site'}
        size="md"
        loading={loading}
        hasUnsavedChanges={hasUnsavedChanges}
        onUnsavedChangesAttempt={() => setShowUnsavedChangesDialog(true)}
        closeOnEscape={false}
        ariaLabelledBy="site-form-title"
      >
        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Nom du site *
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={loading}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all disabled:bg-gray-50 disabled:cursor-not-allowed"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 mt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCloseAttempt}
                disabled={loading}
                className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-all duration-200"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all duration-200 disabled:opacity-50"
              >
                {loading ? 'En cours...' : 'Enregistrer'}
              </button>
          </div>
        </form>
      </Modal>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmation}
        title="Confirmer la modification"
        message="Êtes-vous sûr de vouloir renommer ce site ?"
        confirmLabel="Confirmer"
        cancelLabel="Annuler"
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirmation(false)}
        variant="warning"
      />

      {/* Unsaved changes confirmation */}
      <ConfirmationDialog
        isOpen={showUnsavedChangesDialog}
        title="Modifications non enregistrées"
        message="Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter sans enregistrer ?"
        confirmLabel="Quitter sans enregistrer"
        cancelLabel="Continuer à modifier"
        onConfirm={onClose}
        onCancel={() => setShowUnsavedChangesDialog(false)}
        variant="warning"
      />
    </>
  );
}
